import { merge } from '../helpers/classes';
import { bg100 } from './background';

export const sizeClasses = {
  s: ['text-sm'],
  m: ['text-base'],
  l: ['text-lg'],
};

const sharedClasses = [
  'flex',
  'font-bold',
  'items-center',
  'py-[.5em]',
  'px-[1em]',
  'rounded',
  'justify-center',
];

export type ButtonColors =
  | 'default'
  | 'blue'
  | 'slate'
  | 'olive'
  | 'white'
  | 'red'
  | 'pink'
  | 'cyan'
  | 'orange'
  | 'neutral'
  | 'green'
  | 'transparent';

export const defaultBlue = merge([
  'bg-blue-600',
  'dark:bg-blue-300',
  'disabled:bg-blue-600',
  'dark:disabled:bg-blue-400',

  'hover:bg-blue-700',
  'dark:hover:bg-blue-400',

  'active:bg-blue-800',
  'dark:active:bg-blue-500',

  'dark:text-slate-800',
]);

export const lightBlue = merge([
  'bg-blue-600/10',
  'dark:blue-400/10',
  'disabled:bg-blue-600/10',
  'dark:disabled:bg-blue-600/5',

  'hover:bg-blue-600/20',
  'dark:hover:blue-400/10',

  'active:bg-blue-600/40',
  'dark:active:blue-400/10',

  'dark:text-slate-800',
]);

export const lightestBlue = merge([
  'bg-light-blue-500/10',
  'dark:light-blue-400/10',
  'disabled:bg-light-blue-500/10',
  'dark:disabled:bg-light-blue-500/5',

  'hover:bg-light-blue-600/20',
  'dark:hover:light-blue-400/10',

  'active:bg-light-blue-600/40',
  'dark:active:light-blue-400/10',

  'dark:text-slate-800',
]);

export const defaultOrange = merge([
  'bg-orange-700',
  'dark:bg-orange-300',
  'disabled:bg-orange-700',
  'dark:disabled:bg-orange-300',

  'hover:bg-orange-800',
  'dark:hover:bg-orange-400',

  'active:bg-orange-900',
  'dark:active:bg-orange-500',

  'dark:text-slate-800',
]);

export const defaultOlive = merge([
  'bg-olive-700',
  'dark:bg-olive-300',
  'disabled:bg-olive-700',
  'dark:disabled:bg-olive-300',

  'hover:bg-olive-800',
  'dark:hover:bg-olive-400',

  'active:bg-olive-900',
  'dark:active:bg-olive-500',

  'dark:text-slate-800',
]);

export const defaultGreen = merge([
  'bg-green-700',
  'dark:bg-green-300',
  'disabled:bg-green-700',
  'dark:disabled:bg-green-300',

  'hover:bg-green-800',
  'dark:hover:bg-green-400',

  'active:bg-green-900',
  'dark:active:bg-green-500',

  'dark:text-slate-800',
]);

export const defaultCyan = merge([
  'bg-cyan-700',
  'dark:bg-cyan-300',
  'disabled:bg-cyan-700',
  'dark:disabled:bg-cyan-300',

  'hover:bg-cyan-800',
  'dark:hover:bg-cyan-400',

  'active:bg-cyan-900',
  'dark:active:bg-cyan-500',

  'dark:text-slate-800',
]);

export const defaultPink = merge([
  'bg-pink-600',
  'dark:bg-pink-300',
  'disabled:bg-pink-700',
  'dark:disabled:bg-pink-300',

  'hover:bg-pink-800',
  'dark:hover:bg-pink-400',

  'active:bg-pink-900',
  'dark:active:bg-pink-500',

  'dark:text-slate-800',
]);

export const defaultTeal = merge([
  'bg-teal-700',
  'dark:bg-teal-300',
  'disabled:bg-teal-700',
  'dark:disabled:bg-teal-300',

  'hover:bg-teal-800',
  'dark:hover:bg-teal-400',

  'active:bg-teal-900',
  'dark:active:bg-teal-500',

  'dark:text-slate-800',
]);
export const defaultRed = merge([
  'bg-red-600',
  'dark:bg-red-300',
  'disabled:bg-red-600',
  'dark:disabled:bg-red-400',

  'hover:bg-red-700',
  'dark:hover:bg-red-400',

  'active:bg-red-800',
  'dark:active:bg-red-500',

  'dark:text-slate-800',
]);

export const defaultLavander = merge([
  'bg-lavender-600',
  'dark:bg-lavender-300',
  'disabled:bg-lavender-600',
  'dark:disabled:bg-lavender-400',

  'hover:bg-lavender-700',
  'dark:hover:bg-lavender-400',

  'active:bg-lavender-800',
  'dark:active:bg-lavender-500',

  'dark:text-slate-800',
]);

export const defaultNeutral = merge([
  'bg-neutral-500',
  'dark:bg-neutral-300',
  'disabled:bg-neutral-500',
  'dark:disabled:bg-neutral-300',

  'hover:bg-neutral-600',
  'dark:hover:bg-neutral-400',

  'active:bg-neutral-700',
  'dark:active:bg-neutral-500',

  'dark:text-slate-800',
]);

export const defaultWhite = merge([
  'bg-white',
  'dark:bg-white-300',
  'disabled:bg-slate-300',
  'dark:disabled:bg-slate-300',
  'hover:bg-blue-600',
  'dark:hover:bg-blue-600',
  'active:bg-blue-600',
  'dark:active:bg-blue-600',
  'dark:text-black',
  'border-1',
  'border-white',
  'hover:border-blue-600',
  'dark:border-black',
  'dark:hover:border-blue-600',
  'dark:active:border-blue-600',
]);

const defaultMap = {
  default: defaultBlue,
  olive: defaultOlive,
  blue: defaultBlue,
  pink: defaultPink,
  cyan: defaultCyan,
  orange: defaultOrange,
  neutral: defaultNeutral,
  slate:
    'bg-slate-300 hover:bg-slateHover active:bg-slateActive text-slate-800',
  white: 'bg-white hover:bg-white/90 active:bg-white/80',
  red: 'bg-red-700 hover:bg-red-800 active:bg-red-900',
  green: defaultGreen,
  transparent: '',
};

export const defaultButtonClasses = (color: ButtonColors = 'default') => [
  'text-white',
  defaultMap[color],
  ...sharedClasses,
];

const transparentSlate = merge([
  'hover:bg-slate-300/20',
  'dark:hover:bg-slate-800',

  'active:bg-slate-200',
  'dark:active:bg-slate-700',

  'text-slate-800',
  'dark:text-slate-100',
]);

const transparentBlue = merge([
  'text-blue-600',
  'dark:text-blue-400',
  'dark:disabled:text-blue-400',

  'hover:bg-blue-100',
  'dark:hover:bg-blueHoverDark',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-blue-100',
  'dark:active:bg-blueActiveDark',
]);

const transparentCyan = merge([
  'text-cyan-600',
  'dark:text-cyan-400',
  'dark:disabled:text-cyan-400',

  'hover:bg-cyan-100',
  'dark:hover:bg-blueHoverDark',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-cyan-100',
  'dark:active:bg-blueActiveDark',
]);

const transparentWhite = merge([
  'text-white',
  'dark:text-white',
  'disabled:text-white',
  'dark:disabled:text-white',

  'hover:bg-white/10',
  'dark:hover:bg-white/10',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-white/20',
  'dark:active:bg-white/30',
]);

const transparentWhiteText = merge([
  transparentWhite,

  'hover:hover:bg-[#00315D]',
  'dark:hover:bg-[#00315D]',

  'active:bg-[#00315D]',
  'dark:active:bg-[#00315D]',
  'focus:bg-[#00315D]',
  'dark:focus:bg-[#03315D]',
]);

const transparentOlive = merge([
  'text-olive-700',
  'dark:text-olive-300',
  'disabled:text-olive-700',
  'dark:disabled:text-olive-400',

  'hover:bg-olive-50',
  'dark:hover:bg-[rgba(96,215,185,0.1)]',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-olive-100',
  'dark:active:bg-[rgba(96,215,185,0.15)]',
]);

const transparentRed = merge([
  'text-red-700',
  'dark:text-red-400',
  'disabled:text-red-700',
  'dark:disabled:text-red-400',

  'hover:bg-red-50',
  'dark:hover:bg-redHoverDark',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-red-100',
  'dark:active:bg-redActiveDark',
]);

const transparentPink = merge([
  'text-pink-700',
  'dark:text-pink-400',
  'disabled:text-pink-700',
  'dark:disabled:text-pink-400',

  'hover:bg-pink-50',
  'dark:hover:bg-redHoverDark',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-pink-100',
  'dark:active:bg-redActiveDark',
]);

const transparentOrange = merge([
  'text-orange-700',
  'dark:text-orange-400',
  'disabled:text-orange-700',
  'dark:disabled:text-orange-400',

  'hover:bg-orange-50',
  'dark:hover:bg-redHoverDark',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',

  'active:bg-orange-100',
  'dark:active:bg-redActiveDark',
]);

const transparentNeutral = merge([
  'text-neutral-500',
  'dark:text-neutral-300',
  'disabled:text-neutral-500',
  'dark:disabled:text-neutral-300',

  'hover:bg-neutral-100',
  'dark:hover:bg-[rgba(96,215,185,0.1)]',
  'disabled:hover:bg-transparent',
  'disabled:dark:hover:bg-transparent',
]);

const transparentGreen = merge([
  'text-green-700',
  'dark:text-green-400',
  'disabled:text-green-700',
  'dark:disabled:text-green-400',
  'hover:bg-green-50',
  'dark:hover:bg-green-100',
  'disabled:hover:bg-greenActiveDark',
  'disabled:dark:hover:bg-greenActiveDark',
  'active:bg-greenActiveDark',
  'dark:active:bg-greenActiveDark',
]);

const transparentMap: Record<ButtonColors, string> = {
  olive: transparentOlive,
  default: transparentSlate,
  slate: transparentSlate,
  blue: transparentBlue,
  white: transparentWhite,
  red: transparentRed,
  pink: transparentPink,
  orange: transparentOrange,
  cyan: transparentCyan,
  neutral: transparentNeutral,
  green: transparentGreen,
  transparent: '',
};

const outlineSlate = merge([
  transparentSlate,
  'border-2',
  'border-slate-300',
  'hover:bg-slate-300/40',
  'hover:border-slate-300',
  'active:bg-slate-300/60',
  'dark:border-slate-500',
  'dark:hover:border-slate-950',
  'dark:hover:bg-slate-800',
  'dark:active:border-slate-950',
  'dark:active:bg-slate-950',
]);

export const outlinePink = merge([
  transparentSlate,
  'text-pink-600',
  'border-pink-600',
  'border-2',
  'hover:bg-pink-600/40',
  'hover:border-pink-600',
  'active:bg-pink-600/60',
  'dark:border-pink-500',
  'dark:hover:border-pink-950',
  'dark:hover:bg-pink-800',
  'dark:active:border-pink-950',
  'dark:active:bg-pink-950',
]);

export const outlineOrange = merge([
  transparentSlate,
  'text-orange-700',
  'border-orange-700',
  'border-2',
  'hover:bg-orange-700/40',
  'hover:border-orange-700',
  'active:bg-orange-700/60',
  'dark:border-orange-500',
  'dark:hover:border-orange-950',
  'dark:hover:bg-orange-800',
  'dark:active:border-orange-950',
  'dark:active:bg-orange-950',
]);

export const outlineCyan = merge([
  transparentSlate,
  'text-cyan-700',
  'border-cyan-700',
  'border-2',
  'hover:bg-cyan-700/40',
  'hover:border-cyan-700',
  'active:bg-cyan-700/60',
  'dark:border-cyan-500',
  'dark:hover:border-cyan-950',
  'dark:hover:bg-cyan-800',
  'dark:active:border-cyan-950',
  'dark:active:bg-cyan-950',
]);

export const outlineBlue = merge([
  transparentBlue,
  'border-2',
  'border-blue-600',
  'hover:bg-blue-600/40',
  'hover:border-blue-600',
  'active:bg-blue-600/60',
  'dark:border-blue-400',
  'dark:hover:border-blue-900',
  'dark:hover:bg-blue-700',
  'dark:active:border-blue-900',
  'dark:active:bg-blue-900',
]);

// make an outlineGreen
const outlineGreen = merge([
  transparentGreen,
  'border-2',
  'border-green-600',
  'hover:bg-green-600/40',
  'hover:border-green-600',
  'active:bg-green-600/60',
  'dark:border-green-600',
  'dark:hover:border-green-600',
  'dark:hover:border-green-600',
  'dark:active:bg-green-600',
]);

const outlineOlive = merge([
  transparentOlive,
  'border-2',
  'border-olive-600',
  'hover:bg-olive-600/40',
  'hover:border-olive-600',
  'active:bg-olive-600/60',
  'dark:border-olive-500',
  'dark:hover:border-olive-950',
  'dark:hover:bg-olive-800',
  'dark:active:border-olive-950',
  'dark:active:bg-olive-950',
]);

const outlineWhite = merge([
  transparentWhite,
  'border-2',
  'border-white-300',
  'hover:bg-white-300/40',
  'hover:border-white-300',
  'active:bg-white-300/60',
  'dark:border-white-500',
  'dark:hover:border-white-950',
  'dark:hover:bg-white-800',
  'dark:active:border-white-950',
  'dark:active:bg-white-950',
]);

const outlineRed = merge([
  transparentRed,
  'border-2',
  'border-red-500',
  'hover:bg-red-500/40',
  'hover:border-red-500',
  'active:bg-red-500/60',
  'dark:border-red-500',
  'dark:hover:border-red-950',
  'dark:hover:bg-red-800',
  'dark:active:border-red-950',
  'dark:active:bg-red-950',
]);

const outlineNeutral = merge([
  transparentNeutral,
  'border-2',
  'border-neutral-500',
  'hover:bg-neutral-500/40',
  'hover:border-neutral-500',
  'active:bg-neutral-500/60',
  'dark:border-neutral-500',
  'dark:hover:border-neutral-950',
  'dark:hover:bg-neutral-800',
  'dark:active:border-neutral-950',
]);

const outlineMap = {
  slate: outlineSlate,
  olive: outlineOlive,
  default: outlineSlate,
  blue: outlineBlue,
  white: outlineWhite,
  red: outlineRed,
  pink: outlinePink,
  orange: outlineOrange,
  cyan: outlineCyan,
  neutral: outlineNeutral,
  green: outlineGreen,
  transparent: '',
};

const underlineBlue = merge([
  transparentBlue,
  'border-b-2',
  'border-blue-300',
  'hover:bg-blue-100/40',
  'hover:border-blue-100',
  'active:bg-blueActiveDark',
  'dark:active:bg-blueActiveDark',
]);

const underlineSlate = merge([
  transparentSlate,
  'border-b-2',
  'border-slate-300',
  'hover:bg-slate-300/40',
  'hover:border-slate-200',
  'active:bg-slate-200',
  'dark:active:bg-slate-700',
]);

const underlineOlive = merge([
  transparentOlive,
  'border-b-2',
  'border-olive-300',
  'hover:bg-olive-50/40',
  'hover:border-olive-200',
  'active:bg-olive-100',
  'dark:active:bg-[rgba(96,215,185,0.15)]',
]);

const underlineWhite = merge([
  transparentWhite,
  'border-b-2',
  'border-white/50',
  'hover:bg-white/10',
  'hover:border-white/30',
  'active:bg-white/20',
  'dark:active:bg-white/30',
]);

const underlineRed = merge([
  transparentRed,
  'border-b-2',
  'border-red-300',
  'hover:bg-red-50/40',
  'hover:border-red-200',
  'active:bg-red-100',
  'dark:active:bg-redActiveDark',
]);

const underlinePink = merge([
  transparentPink,
  'border-b-2',
  'border-pink-300',
  'hover:bg-pink-50/40',
  'hover:border-pink-200',
  'active:bg-pink-100',
  'dark:active:bg-redActiveDark',
]);

const underlineCyan = merge([
  transparentCyan,
  'border-b-2',
  'border-cyan-300',
  'hover:bg-cyan-100/40',
  'hover:border-cyan-200',
  'active:bg-cyan-100',
  'dark:active:bg-blueActiveDark',
]);

const underlineOrange = merge([
  transparentOrange,
  'border-b-2',
  'border-orange-300',
  'hover:bg-orange-50/40',
  'hover:border-orange-200',
  'active:bg-orange-100',
  'dark:active:bg-redActiveDark',
]);

const underlineNeutral = merge([
  transparentNeutral,
  'border-b-2',
  'border-neutral-300',
  'hover:bg-neutral-100/40',
  'hover:border-neutral-200',
  'active:bg-neutral-100',
  'dark:active:bg-neutral-700',
]);

const underlineGreen = merge([
  transparentGreen,
  'border-b-2',
  'border-green-300',
  'hover:bg-green-50/40',
  'hover:border-green-200',
  'active:bg-greenActiveDark',
  'dark:active:bg-greenActiveDark',
]);

const underlineMap: Record<ButtonColors, string> = {
  blue: underlineBlue,
  default: underlineBlue,
  slate: underlineSlate,
  olive: underlineOlive,
  white: underlineWhite,
  red: underlineRed,
  pink: underlinePink,
  cyan: underlineCyan,
  orange: underlineOrange,
  neutral: underlineNeutral,
  green: underlineGreen,
  transparent: '',
};

export const transparentButtonClasses = (color: ButtonColors = 'default') => [
  transparentMap[color],
  ...sharedClasses,
];

export const outlineButtonClasses = (color: ButtonColors = 'default') => [
  outlineMap[color],
  ...sharedClasses,
];

// add underline for transparent buttons
export const underlineButtonClasses = (color: ButtonColors = 'blue') => [
  underlineMap[color],
  ...sharedClasses,
];

export const disabledButtonClasses = (color: ButtonColors = 'default') => [
  'opacity-50',
];

// composer themes
export const buttonColorClassMap: { [key: string]: string } = {
  transparent: merge([transparentSlate, 'bg-transparent']),
  'bg-1': merge([bg100, transparentSlate]),
  primaryText: merge([defaultBlue, 'bg-blue-700', 'hover:bg-blue-800']),
  primaryLightest: merge([lightBlue]),
  lightestBlue: merge([lightestBlue]),
  teal: merge(defaultTeal),
  olive: merge([defaultOlive]),
  green: merge(defaultGreen),
  orange: merge([defaultOrange]),
  red: merge([defaultRed]),
  pink: merge([defaultPink]),
  lavender: merge([defaultLavander]),
  legacy: '',
  white: merge([defaultWhite]),
  transparentWhiteText: merge([transparentWhiteText, 'bg-transparent']),
};

// custom button styles
export const dashboardSearchTopics = merge(['bg-[#CCE6FF]', [outlineBlue]]);
export const dashboardAIbutton = merge([
  'bg-[linear-gradient(45deg,rgba(73,98,232,1)_0%,rgba(255,63,164,1)_61%,rgba(255,156,93,1)_100%)]',
  'duration-700',
  'hover:animate-gradientMove',
  [outlineBlue],
]);
